import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CREDIT } from "@/data/site";
import { useBooking } from "@/components/site/BookingContext";

const links = [
  { label: "Corsi", to: "/corsi" },
  { label: "Aziende", to: "/aziende" },
  { label: "Chi siamo", to: "/chi-siamo" },
  { label: "Coworking", to: "/coworking" },
  { label: "Domiciliazione", to: "/domiciliazione" },
  { label: "Contatti", to: "/contatti" },
];

const idFor = (to) => (to === "/" ? "home" : to.replace("/", ""));

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const solid = scrolled || location.pathname !== "/";

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -114 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 max-md:bg-white/95 max-md:backdrop-blur-xl max-md:border-b max-md:border-border ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <a
        href={CREDIT.url}
        target="_blank"
        rel="noreferrer"
        data-testid="credit-top"
        aria-label="Realizzato e progettato da BC Next — visita il sito"
        className="group block bg-[#0B0B0C] text-white border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 h-11 flex items-center justify-center gap-2 text-[12px] sm:text-sm whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-red" aria-hidden="true" />
          <span className="text-white/60 font-medium">Realizzato e progettato da</span>
          <span className="font-heading font-bold tracking-normal text-white group-hover:text-brand-red transition-colors">BC Next</span>
          <span className="hidden sm:inline text-white/40">· Visita il sito</span>
          <span aria-hidden="true" className="text-white/45 transition-transform duration-300 group-hover:translate-x-0.5">→</span>
        </div>
      </a>
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-[74px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src="/logo-treffe.png" alt="treffe — Centro di Formazione 3F" className="h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-${idFor(l.to)}`}
              onClick={() => go(l.to)}
              aria-current={location.pathname === l.to ? "page" : undefined}
              className={`text-sm font-semibold uppercase tracking-widest pb-1 border-b-2 transition-colors ${
                location.pathname === l.to
                  ? "text-brand-red border-brand-red"
                  : "text-brand-black border-transparent hover:text-brand-red"
              }`}
            >
              {l.label}
            </button>
          ))}
          <motion.button
            data-testid="nav-cta"
            onClick={() => { setOpen(false); openBooking(); }}
            animate={{ boxShadow: ["0 0 0 0 rgba(214,40,40,0.5)", "0 0 0 10px rgba(214,40,40,0)"] }}
            transition={{ duration: 1.8, repeat: Infinity }}
            className="bg-brand-red text-white text-sm font-bold uppercase tracking-widest px-6 py-3 rounded-none hover:bg-brand-black transition-colors"
          >
            Iscriviti
          </motion.button>
        </nav>

        <button
          className="lg:hidden text-brand-black p-2 -mr-2 relative z-[62]"
          data-testid="mobile-menu-toggle"
          aria-label="Apri menu"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-white border-b border-border overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {links.map((l) => {
                const active = location.pathname === l.to;
                return (
                  <button
                    key={l.to}
                    data-testid={`mnav-${idFor(l.to)}`}
                    onClick={() => go(l.to)}
                    aria-current={active ? "page" : undefined}
                    className={`text-left text-lg font-bold uppercase tracking-wide pl-3 border-l-4 transition-colors ${
                      active ? "text-brand-red border-brand-red" : "text-brand-black border-transparent"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
              <button
                data-testid="mnav-cta"
                onClick={() => { setOpen(false); openBooking(); }}
                className="bg-brand-red text-white font-bold uppercase tracking-widest px-6 py-3 mt-2"
              >
                Iscriviti
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
